/**
 * API Configuration
 * Manages API base URLs based on environment
 * 
 * For comprehensive environment configuration, see lib/config/environment.js
 */

import envConfig, { getApiBaseUrl as getEnvApiUrl, isProduction, isDevelopment, isStaging } from './config/environment';
import { storage, sessionStorage } from './utils/storage';

// Re-export environment utilities for backward compatibility
export { isProduction, isDevelopment, isStaging };

// Testing mode configuration
export const isTestingMode = () => {
  return envConfig.testing.useMockData;
};

// Clear authentication when entering testing mode
export const clearAuthForTesting = () => {
  if (isTestingMode() && typeof globalThis.window !== 'undefined') {
    if (process.env.NEXT_PUBLIC_ENV === 'development') {
      console.log('🧪 Testing mode: Clearing any existing authentication');
    }
    storage.removeItem('admin_access_token');
    storage.removeItem('admin_refresh_token');
    storage.removeItem('admin_user');
    storage.clear();
  }
};

/**
 * Get the appropriate API base URL based on environment
 * Uses the centralized environment configuration
 * @returns {string} API base URL
 */
export const getApiBaseUrl = () => {
  return getEnvApiUrl();
};

/**
 * Get current environment info for debugging
 */
export const getEnvironmentInfo = () => {
  return {
    environment: envConfig.env,
    NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_ENV: process.env.NEXT_PUBLIC_ENV,
    apiBaseUrl: envConfig.api.baseUrl,
    debugMode: envConfig.features.debugMode,
    testingMode: envConfig.testing.useMockData,
    logging: envConfig.logging
  };
};

/**
 * API Configuration object
 */
export const apiConfig = {
  baseUrl: envConfig.api.baseUrl,
  timeout: envConfig.api.timeout,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }
};

// Log configuration only in development environment
if (isDevelopment() && typeof globalThis.window !== 'undefined') {
  console.log('🔧 API Configuration:', {
    environment: envConfig.env,
    baseUrl: apiConfig.baseUrl,
    timeout: apiConfig.timeout,
    debugMode: envConfig.features.debugMode
  });
}

/**
 * API endpoints organized by domain
 */
export const API_ENDPOINTS = {
  // User Authentication
  auth: {
  },

  // Admin Authentication
  admin: {
    auth: {
      login: '/api/v1/admin/auth/login',
      loginOtp: '/api/v1/admin/auth/login-otp',
      verifyOtp: '/api/v1/admin/auth/verify-otp',
      refresh: '/api/v1/admin/auth/refresh',
      changePassword: '/api/v1/admin/auth/change-password',
    },
    dashboard: {
      // Dashboard overview and statistics
      overview: '/api/v1/admin/dashboard/overview',
      statistics: '/api/v1/admin/dashboard/statistics',
      
      // Users management
      users: '/api/v1/admin/dashboard/users',
      userDetails: (userId) => `/api/v1/admin/dashboard/users/${userId}`,
      userSecurityData: (userId) => `/api/v1/admin/dashboard/users/${userId}/security-data`,
      userTransactions: (userId) => `/api/v1/admin/dashboard/users/${userId}/transactions`,
      
      // Transactions
      transactions: '/api/v1/admin/dashboard/transactions',
      
      // Geographic and analytics data
      userDistribution: '/api/v1/admin/dashboard/user-distribution',
      transactionHeatmap: '/api/v1/admin/dashboard/transaction-heatmap',
      recentActivities: '/api/v1/admin/dashboard/recent-activities'
    },
    exchangeRates: {
      // Exchange rates endpoints
      rates: '/api/v1/admin/exchange-rates',
      historical: '/api/v1/admin/exchange-rates/historical',
      currencies: '/api/v1/admin/exchange-rates/currencies'
    },
    compliance: {
      // KYC & AML endpoints
      kycStatistics: '/api/v1/admin/compliance/kyc/statistics',
      amlRiskDistribution: '/api/v1/admin/compliance/aml/risk-distribution',
      amlFlaggedTimeline: '/api/v1/admin/compliance/aml/flagged-users-timeline',
      amlMonitoring: '/api/v1/admin/compliance/aml/monitoring',
      amlRunCheck: '/api/v1/admin/compliance/aml/run-check',
      
      // SARs endpoints
      sarsTransactions: '/api/v1/admin/compliance/sars/transactions',
      sarsLoginAttempts: '/api/v1/admin/compliance/sars/login-attempts',
      sarsTransactionDetails: (id) => `/api/v1/admin/compliance/sars/transactions/${id}`,
      sarsResolve: (id) => `/api/v1/admin/compliance/sars/transactions/${id}/resolve`
    },
    management: {
      // Admin Management endpoints
      statistics: '/api/v1/admin/management/statistics',
      admins: '/api/v1/admin/management/admins',
      adminDetails: (id) => `/api/v1/admin/management/admins/${id}`,
      adminActivity: (id) => `/api/v1/admin/management/admins/${id}/activity`,
      adminSessions: (id) => `/api/v1/admin/management/admins/${id}/sessions`,
      adminPromote: (id) => `/api/v1/admin/management/admins/${id}/promote`,
      adminPermissions: (id) => `/api/v1/admin/management/admins/${id}/permissions`,
      adminStatus: (id) => `/api/v1/admin/management/admins/${id}/status`,
      sessions: '/api/v1/admin/management/sessions',
      terminateSession: (id) => `/api/v1/admin/management/sessions/${id}`,
      currentProfile: '/api/v1/admin/auth/me'
    },
    supportTickets: {
      // Support Tickets endpoints
      tickets: '/api/v1/admin/support-tickets',
      ticketDetails: (id) => `/api/v1/admin/support-tickets/${id}`,
      updateStatus: (id) => `/api/v1/admin/support-tickets/${id}/status`,
      assignTicket: (id) => `/api/v1/admin/support-tickets/${id}/assign`,
      addReply: (id) => `/api/v1/admin/support-tickets/${id}/reply`,
      updatePriority: (id) => `/api/v1/admin/support-tickets/${id}/priority`,
      statistics: '/api/v1/admin/support-tickets/statistics',
      availableAgents: '/api/v1/admin/support-tickets/agents',
      syncFreshdesk: '/api/v1/admin/support-tickets/sync'
    }
  },

  // Dashboard endpoints (legacy - keeping for backward compatibility)
  dashboard: {
    overview: '/dashboard/overview',
    analytics: '/dashboard/analytics',
    transactions: '/dashboard/transactions/recent',
    stats: '/dashboard/stats/users',
    summary: '/dashboard/summary/financial',
    notifications: '/dashboard/notifications/count',
    activities: '/dashboard/activities/recent',
  }
};

/**
 * Dashboard API configuration
 */
export const DASHBOARD_CONFIG = {
  // Default pagination settings
  pagination: {
    defaultPage: 1,
    defaultLimit: 10,
    maxLimit: 100,
    usersPerPage: 10,
    adminsPerPage: 10,
    transactionsPerPage: 10
  },
  
  // Time periods for dashboard metrics
  timePeriods: ['daily', 'weekly', 'monthly', 'yearly', 'all_time'],
  
  // Transaction statuses
  transactionStatuses: ['completed', 'pending', 'failed', 'cancelled', 'refunded'],
  
  // User types
  userTypes: ['user', 'agent', 'admin'],
  
  // Trend directions
  trendTypes: ['increase', 'decrease', 'stable'],
  
  // Transaction types
  transactionTypes: ['P2P', 'Cash-in', 'Cash-out', 'Bill', 'Fee', 'Commission']
};

export default apiConfig; 